import { SmileOutlined } from '@ant-design/icons'
import Picker from '@emoji-mart/react'
import { categories, useChannels } from '@entities/channel'
import { EmployeeChannelRole, InternetRecourceType } from '@entities/channel/model/types'
import { usePosts } from '@entities/post'
import { SYSTEM_POST_TYPES } from '@shared/lib/constants/constants'
import { CustomTag } from '@shared/ui/custom-tag/custom-tag'
import { MobileSwitch } from '@shared/ui/mobile-switch/mobile-switch'
import { MobileHeader } from '@widgets/mobile-header'
import type { TabsProps } from 'antd'
import { Avatar, Button, Card, ColorPicker, Divider, Flex, Input, Popover, Space, Tabs, theme, Typography } from 'antd'
import type { AggregationColor } from 'antd/es/color-picker/color'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Delete } from 'react-feather'
import { useNavigate } from 'react-router'
import { EmployeesTab } from './employees-tab'
import { OrdFormMobile } from './ord-tab.mobile'
import './styled.scss'

const { Title, Text } = Typography

export const SettingsPage = () => {
    const {
        token: {
            magenta,
            colorWhite,
            red,
            volcano,
            orange,
            gold,
            lime,
            green,
            cyan,
            blue,
            geekblue,
            purple,
            colorTextDescription,
            colorPrimary
        }
    } = theme.useToken()

    const customColors = useMemo(
        () => [magenta, red, volcano, orange, gold, lime, green, cyan, blue, geekblue, purple],
        [blue, cyan, geekblue, gold, green, lime, magenta, orange, purple, red, volcano]
    )

    const {
        actions: { editChannel, getEmployees, updateInfo },
        channelDetails
    } = useChannels()

    const {
        actions: { createType, deleteType }
    } = usePosts({ channelId: channelDetails?.id })

    const [signature, setSignature] = useState('')
    const [checkedSignature, setCheckedSignature] = useState(false)

    const [newPostType, setNewPostType] = useState('')
    const [newPostTypeColor, setNewPostTypeColor] = useState(customColors[0])

    const [isSilentMode, setIsSilentMode] = useState(false)

    useEffect(() => {
        if (channelDetails?.settings) {
            const {
                general: { signature, silenceMode, showSignature }
            } = channelDetails.settings
            setIsSilentMode(!!silenceMode)
            if (signature) {
                setSignature(signature)
                setCheckedSignature(showSignature)
            }
        }
    }, [channelDetails])

    const toggleSilentMode = useCallback(() => {
        if (channelDetails?.id) {
            editChannel({
                id: channelDetails.id,
                data: {
                    settings: { general: { silenceMode: !isSilentMode } }
                }
            })
        }
        setIsSilentMode(!isSilentMode)
    }, [channelDetails?.id, editChannel, isSilentMode])

    const handleColorChange = (color: AggregationColor) => {
        setNewPostTypeColor(color.toHexString())
    }

    const handleGetEmployees = useCallback(() => {
        if (channelDetails?.id) {
            getEmployees({ id: channelDetails.id })
        }
    }, [channelDetails?.id, getEmployees])

    const handleUpdateInfo = useCallback(() => {
        if (channelDetails?.id) {
            updateInfo({ id: channelDetails.id })
        }
    }, [channelDetails?.id, updateInfo])

    const [showEmojiPicker, setShowEmojiPicker] = useState(false)
    const handleEmojiSelect = (emoji: any) => {
        setNewPostType((prev) => prev + emoji.native)
        setShowEmojiPicker(false)
    }

    // Обработчик добавления пользовательского типа поста
    const handleAddPostType = useCallback(async () => {
        if (channelDetails?.id) {
            await createType({
                color: newPostTypeColor,
                name: newPostType,
                channel: channelDetails?.id
            })
            setNewPostType('')
        }
    }, [channelDetails?.id, createType, newPostType, newPostTypeColor])

    const handleAddSignature = useCallback(() => {
        if (channelDetails?.id) {
            if (!checkedSignature) {
                editChannel({
                    id: channelDetails.id,
                    data: {
                        settings: { general: { signature: signature, showSignature: true } }
                    }
                })
            } else {
                editChannel({
                    id: channelDetails.id,
                    data: {
                        settings: { general: { showSignature: false } }
                    }
                })
            }
        }
        setCheckedSignature(!checkedSignature)
    }, [channelDetails?.id, checkedSignature, editChannel, signature])

    const isChannel = useMemo(() => channelDetails?.type === InternetRecourceType.channel, [channelDetails?.type])

    const ComponentRender = useMemo(() => {
        if (!isChannel) return null
        return (
            <>
                <Card size="small" style={{ backgroundColor: colorWhite }}>
                    <Flex gap={16} justify={'space-between'} align="center">
                        <Text style={{ fontSize: '17px' }}>Режим тишины</Text>
                        <MobileSwitch checked={isSilentMode} onChange={toggleSilentMode} />
                    </Flex>
                    {/* Подпись */}
                    <Divider style={{ margin: '10px 0 10px 0' }} />
                    <Flex justify={'space-between'} gap={8} style={{ marginBottom: 5 }}>
                        <Text style={{ fontSize: '17px' }}>Включить подпись</Text>
                        <MobileSwitch checked={checkedSignature} onChange={handleAddSignature} />
                    </Flex>
                    <Input
                        style={{ padding: '10px 10px' }}
                        placeholder={'Введите подпись под постом'}
                        value={signature}
                        onChange={(e) => setSignature(e.target.value)}
                    />
                    <Divider style={{ margin: '10px 0 10px 0' }} />
                    {/* Типы постов */}
                    <Flex vertical gap={16}>
                        <Text style={{ fontSize: '17px' }} strong>
                            Типы постов
                        </Text>
                        <Text style={{ fontSize: '17px' }}>Системные:</Text>
                        {/* Дефолтные */}
                        <Space size={[0, 8]} wrap>
                            <Flex gap={12} wrap>
                                {SYSTEM_POST_TYPES.map((type) => (
                                    <CustomTag color={type.color} key={type.id}>
                                        {type.name}
                                    </CustomTag>
                                ))}
                            </Flex>
                        </Space>
                        <Text style={{ fontSize: '17px' }}>Пользовательские:</Text>
                        {/* Пользовательские */}
                        <Space size={[0, 8]} wrap>
                            <Flex gap={12} wrap>
                                {channelDetails?.settings?.general.postTypes.map(({ name, color, id }) => (
                                    <CustomTag
                                        key={id}
                                        color={color}
                                        onClose={() => {
                                            deleteType(id!)
                                        }}
                                    >
                                        {name}
                                    </CustomTag>
                                ))}
                            </Flex>
                        </Space>

                        {/* Создаем типы, нужно апи? */}
                        <Flex gap={8}>
                            <Space.Compact>
                                <Input
                                    placeholder="Новый тип поста"
                                    value={newPostType}
                                    onChange={(e) => setNewPostType(e.target.value)}
                                    // style={{ width: '33%' }}
                                />
                                <Popover content={<Picker onEmojiSelect={handleEmojiSelect} />}>
                                    <Button
                                        icon={<SmileOutlined />}
                                        onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                                    />
                                </Popover>
                            </Space.Compact>

                            <ColorPicker
                                presets={[
                                    {
                                        label: 'Пользовательские цвета',
                                        colors: customColors
                                    }
                                ]}
                                value={newPostTypeColor}
                                onChange={handleColorChange}
                                disabledAlpha
                            />
                            <Button type="primary" onClick={handleAddPostType} disabled={!newPostType}>
                                Добавить
                            </Button>
                        </Flex>
                    </Flex>
                </Card>
                <Card size="small" style={{ backgroundColor: colorWhite, marginTop: 10 }}>
                    <Flex gap={16} align={'center'}>
                        <Flex vertical gap={8}>
                            <Text style={{ fontSize: '17px' }} strong>
                                Тип ссылки:
                            </Text>
                            <Text style={{ color: colorTextDescription }}>
                                Параметр определяет тип создаваемой ссылки для закупов канала. Учет статистики
                                недоступен для ссылок с заявками в публичный канал.
                            </Text>
                        </Flex>
                    </Flex>
                </Card>
                <Card size="small" style={{ backgroundColor: colorWhite, marginTop: 10 }}>
                    <Flex vertical gap={8}>
                        <Button style={{ padding: '25px' }} type={'primary'} onClick={handleUpdateInfo}>
                            Обновить данные канала
                        </Button>
                        <Text style={{ color: colorTextDescription }}>
                            Займет до 10 минут и обновит название, количество подписчиков и открытую ссылку(если
                            требуется)
                        </Text>
                    </Flex>
                    <Divider style={{ margin: '10px 0 10px 0' }} />
                    <Flex vertical gap={8}>
                        <Button style={{ padding: '25px' }} type={'primary'} onClick={handleGetEmployees}>
                            Выгрузить подписчиков
                        </Button>
                        <Text style={{ color: colorTextDescription }}>
                            Выгрузка информации о подписчиках в виде Exel-таблицы. Это может занять 10 минут.
                        </Text>
                    </Flex>
                </Card>
            </>
        )
    }, [
        channelDetails?.settings?.general.postTypes,
        checkedSignature,
        colorTextDescription,
        colorWhite,
        customColors,
        deleteType,
        handleAddPostType,
        handleAddSignature,
        handleGetEmployees,
        handleUpdateInfo,
        isChannel,
        isSilentMode,
        newPostType,
        newPostTypeColor,
        showEmojiPicker,
        signature,
        toggleSilentMode
    ])

    const navigate = useNavigate()

    const items: TabsProps['items'] = [
        {
            key: '1',
            label: <span style={{ fontSize: '17px' }}>Настройки</span>,
            children: <div style={{ overflow: 'auto' }}>{ComponentRender}</div>
        },
        {
            key: '2',
            label: <span style={{ fontSize: '17px' }}>Сотрудники</span>,
            disabled: channelDetails?.role !== EmployeeChannelRole.owner,
            children: (
                <div style={{ overflow: 'auto' }}>
                    <EmployeesTab />
                </div>
            )
        },
        // {
        //     key: '3',
        //     label: <span style={{ fontSize: '17px' }}>Финансы</span>,
        //     disabled: channelDetails?.role !== EmployeeChannelRole.owner,
        //     children: 'В разработке'
        // },
        {
            key: '4',
            label: <span style={{ fontSize: '17px' }}>ОРД</span>,
            disabled: channelDetails?.role !== EmployeeChannelRole.owner,
            children: (
                <div style={{ overflow: 'auto' }}>
                    <OrdFormMobile />
                </div>
            )
        }
        // {
        //     key: '5',
        //     label: <span style={{ fontSize: '17px' }}>Подписка</span>,
        //     disabled: channelDetails?.role !== EmployeeChannelRole.owner,
        //     children: (
        //         <div style={{ maxHeight: !isMobile ? 'calc(100vh - 540px)' : undefined, overflow: 'auto' }}>
        //             <SubscriptionTab />
        //         </div>
        //     )
        // }
    ]

    console.log('SettingsPage')
    console.log('channelDetails', channelDetails)

    return (
        <>
            <MobileHeader
                title={channelDetails?.name || ''}
                filter={
                    <Button type="text" onClick={() => navigate(-1)}>
                        <Delete color={colorPrimary} />
                    </Button>
                }
            />
            <Card size="small" style={{ flex: 1, overflow: 'auto', borderRadius: 0 }}>
                <Card size="small" style={{ backgroundColor: colorWhite }}>
                    {/* Данные канала */}
                    <Flex justify={'space-between'} align="start">
                        <Flex vertical={true} gap={6} style={{ width: 200 }}>
                            <Title
                                level={4}
                                style={{ margin: 0, cursor: 'pointer' }}
                                onClick={() => window.open(`https://t.me/${channelDetails?.username}`)}
                                italic
                            >
                                @{channelDetails?.name}
                            </Title>
                            <Text italic>@{channelDetails?.username}</Text>
                            <Text>Правообладатель: @{channelDetails?.owner}</Text>

                            <Space size={2}>
                                <Text>{channelDetails?.timezone}</Text>
                            </Space>
                            <Text>{channelDetails?.membersCount} подписчиков</Text>
                            <Text>{categories.find((c) => c.value === channelDetails?.category)?.label}</Text>
                        </Flex>
                        <Avatar shape="circle" size={50} src={channelDetails?.photoUrl} />
                    </Flex>
                </Card>
                {/* Меню закладок */}
                <Tabs defaultActiveKey="1" items={items} onChange={() => {}} />
            </Card>
        </>
    )
}
